import { useEffect, useRef, useState } from 'react';
import { rememberPlace, takePlace } from '../api/infiniteList.ts';

/**
 * Keep a scrolling list's place across leaving it and coming back.
 *
 * Someone who scrolls four hundred rows down, opens a client and presses back
 * must land where they were. Without this they land at the top of a one-batch
 * list, having lost both the rows and the position — which is worse than paging,
 * because with a pager at least the page number was in the URL.
 *
 * **What is restored:** how many rows were mounted, and then the scroll
 * position, once those rows are on screen. Both are needed: restoring the
 * position into a list of 25 rows would simply scroll to its bottom.
 *
 * **What is not:** anything, if the filters differ. The key is the filter set,
 * so returning to a different filter is a different list and starts at the top.
 *
 * ---
 *
 * **Three faults, found on 17 September by reproducing them in a visible tab.**
 * They are recorded because each one looked like a different bug and all three
 * were the same entry going wrong.
 *
 * 1. **The unmount cleanup overwrote the position it was meant to keep.** It
 *    called `rememberPlace(key, window.scrollY, …)` on the way out — but React
 *    Router unmounts a page *after* the next one has scrolled to top, so it
 *    wrote `scrollY: 0` over a position of 39,430 recorded moments earlier.
 *    Coming back therefore landed at the top. **The cleanup no longer records:**
 *    the scroll listener has already recorded continuously, which is why it
 *    exists.
 * 2. **Appends and rows are not the same currency.** See `Place.rows`.
 * 3. **The entry was never consumed**, so one bad write poisoned every later
 *    visit, and when its count exceeded what was mounted the restore called
 *    `loadMore()` on every render — a batch a second until the list was
 *    exhausted, then a jump to the top. `takePlace` consumes it once.
 *
 * **The rules this now keeps.** A target is consumed exactly once. Nothing
 * auto-loads without a live, unconsumed target. A target that cannot be reached
 * stops and says so rather than loading the whole list. Falling back to the top
 * is never silent.
 */

/** Rows to fetch beyond the target before giving up, so a shrunken list still settles. */
const OVERSHOOT_BATCHES = 2;

export function useKeepPlace(
  key: string,
  list: { rows: unknown[]; hasMore: boolean; loadMore: () => void },
  batchSize: number,
): { restoring: boolean; lost: string | null } {
  // The target for this key, taken once. A ref, not state: reading it must not
  // depend on a render, and writing it must not cause one.
  const wanted = useRef<{ scrollY: number; rows: number } | null>(null);
  const settled = useRef(false);
  const asked = useRef(0);
  /** Fixed once, when the restore starts. 0 means "not yet decided". */
  const ceiling = useRef(0);
  /** The row count an ask was last spent at, so one ask buys one batch. */
  const askedAt = useRef(-1);
  const [restoring, setRestoring] = useState(false);
  const [lost, setLost] = useState<string | null>(null);

  // A new key is a different list. Take its target — once — and decide there
  // and then whether anything is pending.
  useEffect(() => {
    const place = takePlace(key);
    wanted.current = place ?? null;
    settled.current = place === undefined;
    asked.current = 0;
    ceiling.current = 0;
    askedAt.current = -1;
    setLost(null);
    setRestoring(place !== undefined);
    // Nothing to restore means the list starts at the top, as a changed filter
    // should. The remembered place is the exception and is handled above.
    if (!place) window.scrollTo({ top: 0, behavior: 'auto' });
  }, [key]);

  // Record continuously while scrolling. NOT on unmount — see fault 1.
  useEffect(() => {
    const onScroll = () => {
      if (!settled.current) return; // a restore in flight is not the reader's position
      rememberPlace(key, window.scrollY, list.rows.length);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [key, list.rows.length]);

  // The restore itself. Depends on the row count and the key — never on `list`,
  // whose identity changes every render and turned a stale target into a loop.
  useEffect(() => {
    const want = wanted.current;
    if (!want || settled.current) return;

    if (list.rows.length < want.rows && list.hasMore) {
      // One ask buys one batch.
      //
      // This effect re-runs on every render, because `loadMore`'s identity
      // changes with the query object behind it. Counting asks alone would let a
      // burst of renders spend the whole budget on calls react-query ignores
      // because a fetch is already in flight — the budget would run out without
      // a single row arriving, and the restore would report the place lost when
      // nothing was wrong. Keyed on the row count, an ask is spent only when the
      // list actually grew.
      if (askedAt.current === list.rows.length) return;

      // Bounded, and the bound comes from the target and the batch size — never
      // from the current row count.
      //
      // Two earlier versions got this wrong in opposite directions. One divided
      // the target by the *current* row count, so the ceiling shrank on every
      // pass and could cut a legitimate deep restore short. Then, dividing only
      // once, it was still read at a moment when the first batch had not landed
      // and `rows.length` was 0 — `Math.max(…, 1)` made the divisor 1 and the
      // ceiling the whole target, which is no bound at all and is exactly the
      // runaway this exists to stop. The batch size is a constant; use it.
      if (ceiling.current === 0) {
        ceiling.current = Math.ceil(want.rows / batchSize) + OVERSHOOT_BATCHES;
      }
      if (asked.current >= ceiling.current) {
        settled.current = true;
        setRestoring(false);
        setLost('Could not get back to where you were — the list has changed. This is the top.');
        window.scrollTo({ top: 0, behavior: 'auto' });
        return;
      }
      askedAt.current = list.rows.length;
      asked.current += 1;
      list.loadMore();
      return;
    }

    if (list.rows.length === 0) return;

    settled.current = true;
    setRestoring(false);

    // The list ran out before reaching the target: say so rather than landing
    // somewhere arbitrary and letting it read as the list having lost its place.
    if (list.rows.length < want.rows) {
      setLost(`Only ${list.rows.length.toLocaleString()} rows are here now, not ${want.rows.toLocaleString()} — this is as close as it gets.`);
    }

    // After the rows are laid out, not before.
    requestAnimationFrame(() => window.scrollTo({ top: want.scrollY, behavior: 'auto' }));
  }, [key, list.rows.length, list.hasMore, list.loadMore]);

  // Record what is mounted, so the next visit knows how far to load. Skipped
  // while a restore is in flight: re-recording then is what let the entry
  // ratchet, each round trip writing a worse value than the last.
  useEffect(() => {
    if (!settled.current) return;
    rememberPlace(key, window.scrollY, list.rows.length);
  }, [key, list.rows.length]);

  return { restoring, lost };
}
